import type { DatabaseEngineId } from '@/core/engines';
import type { SqlSnippetTemplate } from './sqlSnippetTemplateTypes';
import { byEngine } from './sqlSnippetTemplateUtils';

export function getBuiltInSqlSnippetTemplatesDmlSelect(engineId: DatabaseEngineId): readonly SqlSnippetTemplate[] {
  const templates: SqlSnippetTemplate[] = [
    {
      id: 'select-basic',
      label: byEngine(engineId, { mssql: 'SELECT TOP …', pgsql: 'SELECT … LIMIT …', sqlite: 'SELECT … LIMIT …', oracle: 'SELECT … FETCH FIRST …' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, { mssql: 'Basic SELECT template (SQL Server)', pgsql: 'Basic SELECT template (PostgreSQL)', sqlite: 'Basic SELECT template (SQLite)', oracle: 'Basic SELECT template (Oracle)' }),
      filterText: byEngine(engineId, { mssql: 'select top', pgsql: 'select limit', sqlite: 'select limit', oracle: 'select fetch first' }),
      insertText: byEngine(engineId, {
        mssql: 'SELECT TOP ${1:100}\n  ${2:*}\nFROM ${3:table};',
        pgsql: 'SELECT\n  ${1:*}\nFROM ${2:table}\nLIMIT ${3:100};',
        sqlite: 'SELECT\n  ${1:*}\nFROM ${2:table}\nLIMIT ${3:100};',
        oracle: 'SELECT\n  ${1:*}\nFROM ${2:table}\nFETCH FIRST ${3:100} ROWS ONLY;',
      }),
    },
    {
      id: 'select-where-order',
      label: 'SELECT … WHERE … ORDER BY …',
      detail: 'Built-in snippet',
      documentation: 'SELECT with WHERE + ORDER BY template',
      filterText: 'select where order by',
      insertText: byEngine(engineId, {
        mssql: 'SELECT TOP ${1:100}\n  ${2:*}\nFROM ${3:table}\nWHERE ${4:condition}\nORDER BY ${5:column};',
        pgsql: 'SELECT\n  ${1:*}\nFROM ${2:table}\nWHERE ${3:condition}\nORDER BY ${4:column}\nLIMIT ${5:100};',
        sqlite: 'SELECT\n  ${1:*}\nFROM ${2:table}\nWHERE ${3:condition}\nORDER BY ${4:column}\nLIMIT ${5:100};',
        oracle: 'SELECT\n  ${1:*}\nFROM ${2:table}\nWHERE ${3:condition}\nORDER BY ${4:column}\nFETCH FIRST ${5:100} ROWS ONLY;',
      }),
    },
    {
      id: 'select-distinct',
      label: 'SELECT DISTINCT …',
      detail: 'Built-in snippet',
      documentation: 'SELECT DISTINCT template',
      filterText: 'select distinct',
      insertText: byEngine(engineId, {
        mssql: 'SELECT DISTINCT TOP ${1:100}\n  ${2:*}\nFROM ${3:table};',
        pgsql: 'SELECT DISTINCT\n  ${1:*}\nFROM ${2:table}\nLIMIT ${3:100};',
        sqlite: 'SELECT DISTINCT\n  ${1:*}\nFROM ${2:table}\nLIMIT ${3:100};',
        oracle: 'SELECT DISTINCT\n  ${1:*}\nFROM ${2:table}\nFETCH FIRST ${3:100} ROWS ONLY;',
      }),
    },
    {
      id: 'select-pagination',
      label: byEngine(engineId, { mssql: 'SELECT … pagination (OFFSET/FETCH)', pgsql: 'SELECT … pagination (LIMIT/OFFSET)', oracle: 'SELECT … pagination (OFFSET/FETCH)' }),
      detail: 'Built-in snippet',
      documentation: byEngine(engineId, {
        mssql: 'Pagination template using OFFSET/FETCH (SQL Server)',
        pgsql: 'Pagination template using LIMIT/OFFSET (PostgreSQL)',
        sqlite: 'Pagination template using LIMIT/OFFSET (SQLite)',
        oracle: 'Pagination template using OFFSET/FETCH (Oracle 12c+)',
      }),
      filterText: 'select pagination limit offset fetch next',
      insertText: byEngine(engineId, {
        mssql: 'SELECT\n  ${1:*}\nFROM ${2:table}\nORDER BY ${3:column}\nOFFSET ${4:0} ROWS\nFETCH NEXT ${5:100} ROWS ONLY;',
        pgsql: 'SELECT\n  ${1:*}\nFROM ${2:table}\nORDER BY ${3:column}\nLIMIT ${4:100}\nOFFSET ${5:0};',
        sqlite: 'SELECT\n  ${1:*}\nFROM ${2:table}\nORDER BY ${3:column}\nLIMIT ${4:100}\nOFFSET ${5:0};',
        oracle: 'SELECT\n  ${1:*}\nFROM ${2:table}\nORDER BY ${3:column}\nOFFSET ${4:0} ROWS\nFETCH NEXT ${5:100} ROWS ONLY;',
      }),
    },
    {
      id: 'select-join',
      label: 'SELECT … JOIN … ON …',
      detail: 'Built-in snippet',
      documentation: 'SELECT with JOIN template',
      filterText: 'select join on inner left right',
      insertText:
        'SELECT\n  ${1:a.*},\n  ${2:b.*}\nFROM ${3:table_a} a\nJOIN ${4:table_b} b ON ${5:a.id} = ${6:b.a_id};',
    },
    {
      id: 'select-group-by',
      label: 'SELECT … GROUP BY …',
      detail: 'Built-in snippet',
      documentation: 'Aggregate SELECT with GROUP BY + HAVING template',
      filterText: 'select group by having count sum avg',
      insertText:
        'SELECT\n  ${1:group_column},\n  COUNT(*) AS ${2:count}\nFROM ${3:table}\nGROUP BY ${1:group_column}\nHAVING COUNT(*) > ${4:0}\nORDER BY ${2:count} DESC;',
    },
    {
      id: 'select-exists',
      label: 'SELECT … WHERE EXISTS …',
      detail: 'Built-in snippet',
      documentation: 'Correlated EXISTS template',
      filterText: 'select where exists',
      insertText:
        'SELECT\n  ${1:*}\nFROM ${2:table} t\nWHERE EXISTS (\n  SELECT 1\n  FROM ${3:other_table} o\n  WHERE ${4:o.table_id} = ${5:t.id}\n);',
    },
    {
      id: 'cte',
      label: 'CTE (WITH … AS …)',
      detail: 'Built-in snippet',
      documentation: 'Common table expression template',
      filterText: 'with cte',
      insertText:
        'WITH ${1:cte} AS (\n  SELECT\n    ${2:*}\n  FROM ${3:table}\n)\nSELECT\n  ${4:*}\nFROM ${1:cte};',
    },
    {
      id: 'cte-recursive',
      label: byEngine(engineId, { mssql: 'Recursive CTE (WITH …)', pgsql: 'Recursive CTE (WITH RECURSIVE …)', oracle: 'Recursive CTE (WITH …)' }),
      detail: 'Built-in snippet',
      documentation: 'Recursive CTE template (hierarchy traversal)',
      filterText: 'with recursive cte hierarchy',
      insertText: byEngine(engineId, {
        mssql:
          'WITH ${1:cte} AS (\n  SELECT\n    ${2:id},\n    ${3:parent_id}\n  FROM ${4:table}\n  WHERE ${5:parent_id} IS NULL\n\n  UNION ALL\n\n  SELECT\n    t.${2:id},\n    t.${3:parent_id}\n  FROM ${4:table} t\n  JOIN ${1:cte} c ON t.${3:parent_id} = c.${2:id}\n)\nSELECT *\nFROM ${1:cte};',
        pgsql:
          'WITH RECURSIVE ${1:cte} AS (\n  SELECT\n    ${2:id},\n    ${3:parent_id}\n  FROM ${4:table}\n  WHERE ${5:parent_id} IS NULL\n\n  UNION ALL\n\n  SELECT\n    t.${2:id},\n    t.${3:parent_id}\n  FROM ${4:table} t\n  JOIN ${1:cte} c ON t.${3:parent_id} = c.${2:id}\n)\nSELECT *\nFROM ${1:cte};',
        oracle:
          'WITH ${1:cte} (${2:id}, ${3:parent_id}) AS (\n  SELECT\n    ${2:id},\n    ${3:parent_id}\n  FROM ${4:table}\n  WHERE ${5:parent_id} IS NULL\n\n  UNION ALL\n\n  SELECT\n    t.${2:id},\n    t.${3:parent_id}\n  FROM ${4:table} t\n  JOIN ${1:cte} c ON t.${3:parent_id} = c.${2:id}\n)\nSELECT *\nFROM ${1:cte};',
      }),
    },
  ];

  if (engineId === 'pgsql') {
    templates.push({
      id: 'pg19-group-by-all',
      label: 'SELECT … GROUP BY ALL (PG19)',
      detail: 'Built-in snippet',
      documentation: 'Group by every non-aggregated column in the SELECT list without repeating them (PostgreSQL 19+).',
      filterText: 'select group by all pg19',
      insertText: 'SELECT\n  ${1:cols}, count(*)\nFROM ${2:table}\nGROUP BY ALL;',
    });
  }

  if (engineId === 'oracle') {
    templates.push({
      id: 'hierarchical-query',
      label: 'Hierarchical query (CONNECT BY)',
      detail: 'Built-in snippet',
      documentation: 'Oracle hierarchical query using CONNECT BY / START WITH',
      filterText: 'connect by start with prior level',
      insertText:
        'SELECT\n  LEVEL,\n  ${1:id},\n  ${2:parent_id},\n  LPAD(\' \', 2 * (LEVEL - 1)) || ${3:name} AS ${4:tree}\nFROM ${5:table}\nSTART WITH ${2:parent_id} IS NULL\nCONNECT BY PRIOR ${1:id} = ${2:parent_id}\nORDER SIBLINGS BY ${3:name};',
    });
  }

  return templates;
}
